import { useCallback, useState } from 'react';

import { usePairDetails } from '@app/hooks/pair/usePairDetails';
import { usePairData } from '@app/hooks/swap/usePairData';
import { Token } from '@app/types/token';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { ApplicationConfig } from '@app/config/types';
import { useConfig } from '@app/config';

export type InputsState = {
  from: {
    token: Token | null;
    amount: string;
  };
  to: {
    token: Token | null;
    amount: string;
  };
};

function getAddressToCompare(
  address: string | undefined,
  config: ApplicationConfig | undefined
) {
  if (address === NATIVE_TOKEN_ADDRESS) {
    return config?.CONTRACTS.WETH!;
  }

  return address;
}

export function useAddLiquidityPageData(initialData?: InputsState) {
  const config = useConfig();

  const [state, setState] = useState<InputsState>(
    initialData ?? {
      from: {
        token: null,
        amount: ''
      },
      to: {
        token: null,
        amount: ''
      }
    }
  );

  const { data: pairAddress } = usePairData(
    state.from.token?.contractAddress ?? '',
    state.to.token?.contractAddress ?? ''
  );

  const { data: pairDetails } = usePairDetails({
    id: pairAddress ? pairAddress.toLowerCase() : null
  });

  const resetStateValues = useCallback(() => {
    setState(prev => ({
      ...prev,
      from: {
        ...prev.from,
        amount: ''
      },
      to: {
        ...prev.to,
        amount: ''
      }
    }));
  }, []);

  const handleFromTokenChange = useCallback(
    (val: string) => {
      setState(prev => {
        const newValue = {
          ...prev,
          from: {
            ...prev.from,
            amount: +val < 0 ? '0' : val
          }
        };

        if (pairDetails) {
          let [reserve0, reserve1] =
            getAddressToCompare(
              prev.from.token?.contractAddress,
              config
            )?.toLowerCase() === pairDetails.token0.id.toLowerCase()
              ? [pairDetails.reserve0, pairDetails.reserve1]
              : [pairDetails.reserve1, pairDetails.reserve0];
          const amountTo =
            val !== '' && +val >= 0 && +reserve0 > 0
              ? (+val * +reserve1) / +reserve0
              : '';

          newValue.to = {
            ...prev.to,
            amount: amountTo ? amountTo.toFixed(6) : prev.to.amount
          };
        }

        return newValue;
      });
    },
    [pairDetails, config]
  );

  const handleToTokenChange = useCallback(
    (val: string) => {
      // amountB = amountA.mul(reserveB) / reserveA;
      setState(prev => {
        const newValue = {
          ...prev,
          to: {
            ...prev.to,
            amount: +val < 0 ? '0' : val
          }
        };

        if (pairDetails) {
          let [reserve0, reserve1] =
            getAddressToCompare(
              prev.to.token?.contractAddress,
              config
            )?.toLowerCase() === pairDetails.token0.id.toLowerCase()
              ? [pairDetails.reserve0, pairDetails.reserve1]
              : [pairDetails.reserve1, pairDetails.reserve0];

          const amountFrom =
            val !== '' && +val >= 0 && +reserve0 > 0
              ? (+val * +reserve1) / +reserve0
              : '';

          newValue.from = {
            ...prev.from,
            amount: amountFrom ? amountFrom.toFixed(6) : prev.from.amount
          };
        }

        return newValue;
      });
    },
    [pairDetails, config]
  );

  const handleFromTokenSelect = useCallback(
    (token: Token) => {
      setState(prev => ({
        ...prev,
        from: {
          ...prev.from,
          token,
          amount: ''
        },
        to: {
          token:
            (token.contractAddress === config?.CONTRACTS.WETH &&
              prev.to.token?.contractAddress === NATIVE_TOKEN_ADDRESS) ||
            (token.contractAddress === NATIVE_TOKEN_ADDRESS &&
              prev.to.token?.contractAddress === config?.CONTRACTS.WETH) ||
            token.contractAddress === prev.to.token?.contractAddress
              ? null
              : prev.to.token,
          amount: ''
        }
      }));
    },
    [config]
  );

  const handleToTokenSelect = useCallback(
    (token: Token) => {
      setState(prev => ({
        ...prev,
        to: {
          ...prev.to,
          token,
          amount: ''
        },
        from: {
          token:
            (token.contractAddress === config?.CONTRACTS.WETH &&
              prev.from.token?.contractAddress === NATIVE_TOKEN_ADDRESS) ||
            (token.contractAddress === NATIVE_TOKEN_ADDRESS &&
              prev.from.token?.contractAddress === config?.CONTRACTS.WETH) ||
            token.contractAddress === prev.from.token?.contractAddress
              ? null
              : prev.from.token,
          amount: ''
        }
      }));
    },
    [config]
  );

  return {
    state,
    setState,
    resetStateValues,
    handleFromTokenChange,
    handleToTokenChange,
    handleFromTokenSelect,
    handleToTokenSelect,
    pairAddress
  };
}
